import type {
  HealthStatus,
  PortfolioProjectRow,
  PortfolioQueryParams,
  PortfolioSortField,
  PortfolioStats,
  PortfolioStatusPreset,
  SortDirection,
} from '@/api/portfolio/api';
import type { AuthenticatedUser } from '@/models/auth.model';
import { alphanumericCompare } from '@/utils/alphanumericSort';

const DIRECTOR_TITLE_PATTERN =
  /director|vice president|\bvp\b|chief|c[teo]o|head of|principal|partner|managing/i;
const DIRECTOR_ROLES = new Set(['DIRECTOR', 'VP', 'EXECUTIVE', 'C_SUITE', 'ADMIN']);

/**
 * Mirrors the back-end `AuthenticatedUser.is_director_or_higher` gate: Director+
 * users may see financial columns. Non-management users see nothing (Story 5).
 */
export const isDirectorOrHigher = (user: AuthenticatedUser | null | undefined): boolean => {
  if (!user) {
    return false;
  }
  if (DIRECTOR_ROLES.has((user.assumedRole ?? '').toUpperCase())) {
    return true;
  }
  return DIRECTOR_TITLE_PATTERN.test(user.jobTitle ?? '');
};

const PM_TITLE_PATTERN =
  /product manager|project manager|program manager|programme manager|delivery manager/i;

// Back-end functional group names (always uppercase, from JWT claims).
const PM_FUNCTIONAL_GROUPS = new Set([
  'PROJECT MANAGEMENT',
  'PRODUCT MANAGEMENT',
  'CLIENT MANAGEMENT',
]);

/**
 * Mirrors the back-end `is_product_management || is_project_management` gate.
 * Product Managers and Project Managers share the same metric-access level as Directors.
 *
 * Checks both `jobTitle` (regex, same pattern as the BE title matcher) and
 * `functionalGroups` (JWT-provided, computed by BE from job_title + department
 * aliases).  The group check catches roles like Scrum Master and PMO analyst
 * whose title does not match the pattern but whose department resolves to a PM
 * functional group on the back end.
 */
export const isProductOrProjectManager = (user: AuthenticatedUser | null | undefined): boolean => {
  if (!user) {
    return false;
  }
  if (PM_TITLE_PATTERN.test(user.jobTitle ?? '')) {
    return true;
  }
  return user.functionalGroups.some((g) => PM_FUNCTIONAL_GROUPS.has(g.toUpperCase()));
};

/** Worst-first severity ranking. Red beats Yellow beats Green; Pending is lowest. */
export const HEALTH_SEVERITY: Record<HealthStatus, number> = {
  red: 3,
  yellow: 2,
  green: 1,
  pending: 0,
};

const MISSING_UPDATE_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Computes a project's Overall Status as the worst of its health indicators.
 * Returns `pending` when no weekly update has ever been filed (Story 3 AC-2).
 */
export const rollupOverallStatus = (
  onSchedule: HealthStatus,
  csat: HealthStatus,
  teamHealth: HealthStatus,
  hasUpdate: boolean,
): HealthStatus => {
  if (!hasUpdate) {
    return 'pending';
  }
  const worst = [onSchedule, csat, teamHealth].reduce<HealthStatus>((acc, indicator) => {
    if (indicator === 'pending') {
      return acc;
    }
    return HEALTH_SEVERITY[indicator] > HEALTH_SEVERITY[acc] ? indicator : acc;
  }, 'green');
  return worst;
};

/** True when the last weekly update is older than 7 days (Story 4 AC-1). */
export const isMissingUpdate = (lastWeeklyUpdateAt: string | null, now: number): boolean => {
  if (!lastWeeklyUpdateAt) {
    return true;
  }
  const parsed = Date.parse(lastWeeklyUpdateAt);
  if (!Number.isFinite(parsed)) {
    return true;
  }
  return now - parsed > MISSING_UPDATE_THRESHOLD_MS;
};

const normalize = (value: string): string => value.trim().toLocaleLowerCase();

const matchesSearch = (row: PortfolioProjectRow, query: string): boolean => {
  const needle = normalize(query);
  if (!needle) {
    return true;
  }
  return [row.client_name, row.project_name, row.id, row.snowflake_project_id ?? '']
    .map(normalize)
    .some((value) => value.includes(needle));
};

const matchesMultiSelect = (value: string, selected?: string[]): boolean =>
  !selected || selected.length === 0 || selected.includes(value);

/**
 * Applies every filter EXCEPT the stat-card status preset. Stats are computed
 * from this set so switching between presets stays coherent.
 */
export const filterPortfolioRows = (
  rows: PortfolioProjectRow[],
  params: PortfolioQueryParams,
): PortfolioProjectRow[] =>
  rows.filter((row) => {
    // `client_id`/`project_id` filter values hold display-name strings selected
    // from the dropdowns (see `buildFilterOptionsFromRows`, `value === label`),
    // matched against the real API's exact `client_name`/`project_name` filters
    // — not the rows' actual `client_id`/`id` fields.
    if (!matchesMultiSelect(row.client_name, params.client_id)) {
      return false;
    }
    if (!matchesMultiSelect(row.project_name, params.project_id)) {
      return false;
    }
    if (!matchesMultiSelect(row.offering, params.offering)) {
      return false;
    }
    if (!matchesMultiSelect(row.delivery_lead_name, params.leader)) {
      return false;
    }
    if (params.sow_status && params.sow_status !== 'all' && row.sow_status !== params.sow_status) {
      return false;
    }
    if (params.overall_status && row.overall_status !== params.overall_status) {
      return false;
    }
    if (params.on_schedule && row.on_schedule !== params.on_schedule) {
      return false;
    }
    if (params.csat && row.csat !== params.csat) {
      return false;
    }
    if (params.team_health && row.team_health !== params.team_health) {
      return false;
    }
    if (params.project_name && !matchesSearch(row, params.project_name)) {
      return false;
    }
    if (
      params.starts_on_before &&
      Date.parse(row.start_date) > Date.parse(params.starts_on_before)
    ) {
      return false;
    }
    if (params.ends_on_after && Date.parse(row.end_date) < Date.parse(params.ends_on_after)) {
      return false;
    }
    return true;
  });

/** Which stat bucket a row's Overall Status falls into (null for Pending). */
export const getStatusBucket = (
  row: PortfolioProjectRow,
): Exclude<PortfolioStatusPreset, 'missing_update'> | null => {
  switch (row.overall_status) {
    case 'green':
      return 'on_track';
    case 'yellow':
      return 'watching';
    case 'red':
      return 'at_risk';
    default:
      return null;
  }
};

/** Keeps only rows matching the clicked stat-card preset (Story 1 AC-3). */
export const applyStatusPreset = (
  rows: PortfolioProjectRow[],
  preset: PortfolioStatusPreset | null | undefined,
): PortfolioProjectRow[] => {
  if (!preset) {
    return rows;
  }
  if (preset === 'missing_update') {
    return rows.filter((row) => row.missing_update_flag);
  }
  return rows.filter((row) => getStatusBucket(row) === preset);
};

/** Counts rows into the four stat-card buckets. */
export const computePortfolioStats = (rows: PortfolioProjectRow[]): PortfolioStats => {
  const stats: PortfolioStats = { on_track: 0, watching: 0, at_risk: 0, missing_update: 0 };
  for (const row of rows) {
    const bucket = getStatusBucket(row);
    if (bucket) {
      stats[bucket] += 1;
    }
    if (row.missing_update_flag) {
      stats.missing_update += 1;
    }
  }
  return stats;
};

/** Missing/unparseable dates sort first, mirroring the backend's `date.min` fallback. */
const parseDateOrMin = (value: string): number => {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : -Infinity;
};

const compareByField = (
  left: PortfolioProjectRow,
  right: PortfolioProjectRow,
  field: PortfolioSortField,
): number => {
  switch (field) {
    case 'overall_status':
      return HEALTH_SEVERITY[left.overall_status] - HEALTH_SEVERITY[right.overall_status];
    case 'start_date':
      return parseDateOrMin(left.start_date) - parseDateOrMin(right.start_date);
    case 'end_date':
      return parseDateOrMin(left.end_date) - parseDateOrMin(right.end_date);
    default:
      return alphanumericCompare(left[field] as string, right[field] as string);
  }
};

/** Sorts rows by a column. Status sort is worst-first when descending. */
export const sortPortfolioRows = (
  rows: PortfolioProjectRow[],
  field: PortfolioSortField,
  direction: SortDirection,
): PortfolioProjectRow[] => {
  const next = [...rows];
  next.sort((left, right) => {
    const compareValue = compareByField(left, right, field);
    if (compareValue === 0) {
      return alphanumericCompare(left.project_name, right.project_name);
    }
    return direction === 'asc' ? compareValue : -compareValue;
  });
  return next;
};

/** Slices rows to a single page (1-indexed). */
export const paginatePortfolioRows = (
  rows: PortfolioProjectRow[],
  page: number,
  perPage: number,
): PortfolioProjectRow[] => {
  const safePage = Math.max(1, page);
  const safePerPage = Math.max(1, perPage);
  const startIndex = (safePage - 1) * safePerPage;
  return rows.slice(startIndex, startIndex + safePerPage);
};

/** Removes Director-only financial fields from a row (mirrors the server field gate). */
export const stripFinancialFields = (row: PortfolioProjectRow): PortfolioProjectRow => {
  const next = { ...row };
  delete next.ode_budget;
  delete next.ode_margin;
  return next;
};
